import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // Import useParams and useNavigate
import apiService from '../services/api';
import { useAppContext } from '../context/AppContext';
import CopyableId from '../components/common/CopyableId'; // Import CopyableId
import SaveButton from '../components/common/SaveButton'; // Import SaveButton
import '../styles/SportsOrgsPage.css'; // We'll create this file later

const SportsOrgsPage = () => {
  const { genreName: urlGenreName, orgName: urlOrgName } = useParams(); // Get params from URL
  const navigate = useNavigate(); // Hook for programmatic navigation
  const {
    t,
    loadProgramById,
    sportsGenre, setSportsGenre,
    sportsOrg, setSportsOrg,
    sportsConnector, setSportsConnector,
    sportsSeason, setSportsSeason,
    favouriteSports // Get favourite sports from context
  } = useAppContext();
  const [genres, setGenres] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [conferences, setConferences] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetails, setOrgDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20, // Default items per page
    total: 0,
    total_pages: 0,
    sort_by: 'gameDate', // Default sort field
    sort_order: 'asc'   // Default sort direction
  });
  // Keep separate sort state for UI interaction before API call
  const [sortField, setSortField] = useState('gameDate');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showConferences, setShowConferences] = useState(false); // State for conference visibility
  const [currentPage, setCurrentPage] = useState(1); // State for current page

  // Initialize local state from shared state
  useEffect(() => {
    if (sportsGenre) setSelectedGenre(sportsGenre);
    if (sportsOrg) setSelectedOrg(sportsOrg);
    if (sportsConnector) setSelectedConnector(sportsConnector);
    if (sportsSeason) setSelectedSeason(sportsSeason);
  }, [sportsGenre, sportsOrg, sportsConnector, sportsSeason]);

  // Function to fetch programs with pagination/sorting
  const fetchProgramsForSeason = async (connectorId, season, page, sortBy, sortOrder) => {
    if (!connectorId || !season) return;

    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page,
        limit: pagination.limit, // Use limit from state
        sort_by: sortBy,
        sort_order: sortOrder
      };
      const data = await apiService.getProgramsBySeason(connectorId, season, params);

      if (data && data.programs && data.pagination) {
        setPrograms(data.programs);
        setPagination(data.pagination); // Update pagination state from API response
        setCurrentPage(data.pagination.page); // Sync currentPage state
        // Update local sort state to match response
        setSortField(data.pagination.sort_by);
        setSortDirection(data.pagination.sort_order.toLowerCase());
        console.log('Fetched programs:', data);
      } else {
        console.error('Unexpected API response structure:', data);
        setError(t('sports.errors.unexpectedFormat'));
        setPrograms([]);
        setPagination({ page: 1, limit: 20, total: 0, total_pages: 0, sort_by: 'gameDate', sort_order: 'asc' });
        setCurrentPage(1);
      }
    } catch (err) {
      console.error(`Error fetching programs for season ${season}, page ${page}:`, err);
      setError(t('sports.errors.loadPrograms'));
      setPrograms([]);
      setPagination(prev => ({ ...prev, total: 0, total_pages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  // Fetch seasons when connector changes
  useEffect(() => {
    if (!selectedConnector) {
      setSeasons([]);
      return;
    }
    const fetchSeasonsData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getSeasonsForConnector(selectedConnector.connectorid);
        setSeasons(data);
        console.log('Fetched seasons:', data);
        // If a season was previously selected (from context/URL), fetch its programs
        if (selectedSeason) {
          fetchProgramsForSeason(
            selectedConnector.connectorid,
            selectedSeason,
            1, // Start on page 1
            pagination.sort_by, // Use initial sort state
            pagination.sort_order
          );
        }
      } catch (err) {
        console.error('Error fetching seasons for connector:', err);
        setError(t('sports.errors.loadSeasons'));
        const sampleSeasons = {
          "connectorid": selectedConnector.connectorid,
          "program_count": 256,
          "seasons": ["2021", "2022", "2023", "2024"]
        };
        setSeasons(sampleSeasons);
      } finally {
        setLoading(false);
      }
    };
    fetchSeasonsData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnector]); // Only trigger when connector changes

  // Fetch sports genres on component mount
  useEffect(() => {
    const fetchGenres = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getSportsGenres();
        setGenres(data);
        console.log('Fetched genres:', data);
        if (urlGenreName && data && data.length > 0) {
          const decodedGenreName = decodeURIComponent(urlGenreName).toLowerCase();
          const foundGenre = data.find(g => g.genre.toLowerCase() === decodedGenreName);
          if (foundGenre && !selectedGenre) {
            console.log('Setting genre from URL:', foundGenre);
            setSelectedGenre(foundGenre);
            setSportsGenre(foundGenre);
          }
        }
      } catch (err) {
        console.error('Error fetching sports genres:', err);
        setError(t('sports.errors.loadGenres'));
        const sampleGenres = [{ genre: "Error", genreId: 1 }];
        setGenres(sampleGenres);
      } finally {
        setLoading(false);
      }
    };
    fetchGenres();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlGenreName, setSportsGenre]);

  // Handle quick selection (genre + organization combo)
  const handleQuickSelection = async (genreName, orgName) => {
    if (loading || genres.length === 0) return;

    // Find the genre
    const genre = genres.find(g => g.genre.toLowerCase() === genreName.toLowerCase());
    if (!genre) {
      console.warn(`Quick selection genre "${genreName}" not found in available genres.`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Set the genre first
      setSelectedGenre(genre);
      setSportsGenre(genre);
      
      // Clear existing selections
      setSelectedOrg(null); setSportsOrg(null);
      setOrgDetails(null); setConferences([]); setConnectors([]);
      setSelectedConnector(null); setSportsConnector(null);
      setSeasons([]); setSelectedSeason(null); setSportsSeason(null);
      setPrograms([]);
      setPagination(prev => ({ ...prev, page: 1, total: 0, total_pages: 0 }));
      setCurrentPage(1);

      // Fetch organizations for this genre
      const orgsData = await apiService.getSportsOrganizations(genre.genreId);
      setOrganizations(orgsData);
      
      // Find and select the specific organization
      const org = orgsData.find(o => o.name.toLowerCase() === orgName.toLowerCase());
      if (org) {
        // Set the organization selection
        setSelectedOrg(org);
        setSportsOrg(org);
        console.log(`Quick selection: Selected ${genreName} -> ${orgName}`);
        
        // The useEffect for selectedOrg will handle fetching organization details, conferences, and connectors
      } else {
        console.warn(`Quick selection organization "${orgName}" not found in organizations for genre "${genreName}".`);
        console.log('Available organizations:', orgsData.map(o => o.name));
      }
    } catch (err) {
      console.error('Error during quick selection:', err);
      setError(t('sports.errors.loadQuickSelect'));
    } finally {
      setLoading(false);
    }
  };

  // Enhanced URL parameter handling for deep linking
  useEffect(() => {
    if (urlGenreName && urlOrgName && genres.length > 0 && !selectedGenre && !selectedOrg) {
      // Handle deep linking with both genre and organization
      const decodedGenreName = decodeURIComponent(urlGenreName).toLowerCase();
      const decodedOrgName = decodeURIComponent(urlOrgName).toLowerCase();
      
      const foundGenre = genres.find(g => g.genre.toLowerCase() === decodedGenreName);
      if (foundGenre) {
        console.log('Deep linking: Setting genre from URL:', foundGenre);
        handleQuickSelection(foundGenre.genre, decodeURIComponent(urlOrgName));
      }
    }
  }, [urlGenreName, urlOrgName, genres, selectedGenre, selectedOrg, handleQuickSelection]);

  // Fetch organizations when a genre is selected
  useEffect(() => {
    if (!selectedGenre) {
      setOrganizations([]);
      return;
    }
    const fetchOrganizations = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getSportsOrganizations(selectedGenre.genreId);
        setOrganizations(data);
        if (urlOrgName && data && data.length > 0 && !selectedOrg) {
          const decodedOrgName = decodeURIComponent(urlOrgName).toLowerCase();
          const foundOrg = data.find(o => o.name.toLowerCase() === decodedOrgName);
          if (foundOrg) {
            console.log('Setting organization from URL:', foundOrg);
            setSelectedOrg(foundOrg);
            setSportsOrg(foundOrg);
          }
        }
      } catch (err) {
        console.error('Error fetching sports organizations:', err);
        setError(t('sports.errors.loadOrganizations'));
      } finally {
        setLoading(false);
      }
    };
    fetchOrganizations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGenre, urlOrgName, setSportsOrg]);

  // Handle genre selection
  const handleGenreChange = (e) => {
    const genreId = parseInt(e.target.value, 10);
    const genre = genres.find(g => g.genreId === genreId);
    setSelectedGenre(genre);
    setSportsGenre(genre);
    setSelectedOrg(null); setSportsOrg(null);
    setOrgDetails(null); setConferences([]); setConnectors([]);
    setSelectedConnector(null); setSportsConnector(null);
    setSeasons([]); setSelectedSeason(null); setSportsSeason(null);
    setPrograms([]);
    setPagination(prev => ({ ...prev, page: 1, total: 0, total_pages: 0 }));
    setCurrentPage(1);
  };

  // Handle favourite sport button click
  const handleFavouriteSportClick = (sportName) => {
    const genre = genres.find(g => g.genre.toLowerCase() === sportName.toLowerCase().trim());
    if (genre) {
      setSelectedGenre(genre); setSportsGenre(genre);
      setSelectedOrg(null); setSportsOrg(null);
      setOrgDetails(null); setConferences([]); setConnectors([]);
      setSelectedConnector(null); setSportsConnector(null);
      setSeasons([]); setSelectedSeason(null); setSportsSeason(null);
      setPrograms([]);
      setPagination(prev => ({ ...prev, page: 1, total: 0, total_pages: 0 }));
      setCurrentPage(1);
    } else {
      console.warn(`Favourite sport genre "${sportName}" not found in available genres.`);
    }
  };

  // Handle organization selection
  const handleOrgChange = (e) => {
    const orgId = parseInt(e.target.value, 10);
    const org = organizations.find(o => o.organizationId === orgId);
    setSelectedOrg(org); setSportsOrg(org);
    setConferences([]); setOrgDetails(null); setShowConferences(false);
    setConnectors([]); setSelectedConnector(null); setSportsConnector(null);
    setSeasons([]); setSelectedSeason(null); setSportsSeason(null);
    setPrograms([]);
    setPagination(prev => ({ ...prev, page: 1, total: 0, total_pages: 0 }));
    setCurrentPage(1);
  };

  // Open image popup
  const openImagePopup = (asset) => setSelectedImage(asset);
  // Close image popup
  const closeImagePopup = () => setSelectedImage(null);

  // Fetch organization details, conferences, and connectors when an organization is selected
  useEffect(() => {
    if (!selectedOrg) {
      setOrgDetails(null);
      setConferences([]);
      setConnectors([]);
      setSelectedConnector(null); // Also clear selected connector
      return;
    }

    const fetchOrgData = async () => {
      setLoading(true); // Set loading true at the start of combined fetch
      setError(null);
      setOrgDetails(null); // Clear previous details
      setConferences([]); // Clear previous conferences
      setConnectors([]); // Clear previous connectors
      setSelectedConnector(null); // Clear selected connector

      try {
        // Fetch details, conferences, and connectors in parallel
        const [detailsData, conferencesData, connectorsData] = await Promise.all([
          apiService.getSportsOrganizationDetails(selectedOrg.organizationId),
          apiService.getSportsConferences(selectedOrg.organizationId),
          selectedOrg.rootId ? apiService.getSportsConnectors(selectedOrg.rootId) : Promise.resolve([]) // Only fetch if rootId exists
        ]);

        setOrgDetails(detailsData);
        console.log('Fetched organization details:', detailsData);

        setConferences(conferencesData);
        console.log('Fetched conferences:', conferencesData);
        
        if (selectedOrg.rootId) {
            setConnectors(connectorsData);
            console.log('Fetched connectors:', connectorsData);
        } else {
            setConnectors([]); // Ensure connectors are empty if no rootId
        }

      } catch (err) {
        console.error('Error fetching organization data (details/conferences/connectors):', err);
        // Set a general error, or handle specific errors if needed
        setError(t('sports.errors.loadOrgData'));
        // Optionally add sample data here for specific error cases if desired for testing
      } finally {
        setLoading(false); // Set loading false after all fetches complete or fail
      }
    };

    fetchOrgData();
  }, [selectedOrg]); // Depend only on selectedOrg

  // Handle connector selection - simplified, only selects
  const handleConnectorClick = (connector) => {
    // Only act if a different connector is clicked or none is selected
    if (!selectedConnector || selectedConnector.connectorid !== connector.connectorid) {
        setSelectedConnector(connector); 
        setSportsConnector(connector);
        // Reset season/programs when a new connector is selected
        setSelectedSeason(null); 
        setSportsSeason(null);
        setPrograms([]); 
        setPagination(prev => ({ ...prev, page: 1, total: 0, total_pages: 0 })); 
        setCurrentPage(1);
        // Fetching seasons is handled by the useEffect watching selectedConnector
    }
    // If the same connector is clicked, do nothing (no toggle)
  };

  // Handle clearing the selected connector
  const handleClearConnectorSelection = () => {
    setSelectedConnector(null); 
    setSportsConnector(null);
    setSelectedSeason(null); 
    setSportsSeason(null);
    setPrograms([]); 
    setPagination(prev => ({ ...prev, page: 1, total: 0, total_pages: 0 })); 
    setCurrentPage(1);
    setSeasons([]); // Also clear seasons list as it depends on the connector
  };

  // Handle season selection
  const handleSeasonClick = (seasonData) => {
    if (!selectedConnector) return;
    const season = typeof seasonData === 'object' ? seasonData.season : seasonData;

    if (selectedSeason === season) {
      setSelectedSeason(null); setSportsSeason(null);
      setPrograms([]); setPagination(prev => ({ ...prev, page: 1, total: 0, total_pages: 0 })); setCurrentPage(1);
    } else {
      setSelectedSeason(season); setSportsSeason(season);
      // Fetch page 1 with default sort when season changes
      fetchProgramsForSeason(selectedConnector.connectorid, season, 1, 'gameDate', 'asc');
      // Reset local sort state as well
      setSortField('gameDate');
      setSortDirection('asc');
    }
  };

  // Handle column sort - triggers API refetch
  const handleSort = (field) => {
    let newDirection = 'asc';
    if (field === sortField) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      newDirection = 'asc'; // Default to asc for new column
    }
    setSortField(field);
    setSortDirection(newDirection);

    // Refetch data with new sort parameters, staying on the current page
    if (selectedConnector && selectedSeason) {
      fetchProgramsForSeason(selectedConnector.connectorid, selectedSeason, currentPage, field, newDirection);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages && newPage !== currentPage) {
      setCurrentPage(newPage); // Update local page state first
      if (selectedConnector && selectedSeason) {
        fetchProgramsForSeason(selectedConnector.connectorid, selectedSeason, newPage, sortField, sortDirection);
      }
    }
  };

  // Function to update URL with current selections
  const updateUrl = (genre, org) => {
    if (genre && org) {
      const encodedGenre = encodeURIComponent(genre.genre);
      const encodedOrg = encodeURIComponent(org.name);
      navigate(`/sports/${encodedGenre}/${encodedOrg}`, { replace: true });
    } else if (!genre && !org) {
      // If both are cleared, go back to base sports page
      navigate('/sports', { replace: true });
    }
    // If only one is selected, we don't update the URL (incomplete selection)
  };

  // Update URL when both genre and organization are selected
  useEffect(() => {
    // Don't update URL if we're currently processing URL parameters from a page load/refresh
    if (urlGenreName && urlOrgName && (!selectedGenre || !selectedOrg)) {
      // We're in the middle of processing URL parameters, don't interfere
      return;
    }

    if (selectedGenre && selectedOrg) {
      // Only update URL if it's different from current URL
      const currentGenre = urlGenreName ? decodeURIComponent(urlGenreName) : '';
      const currentOrg = urlOrgName ? decodeURIComponent(urlOrgName) : '';
      
      if (selectedGenre.genre !== currentGenre || selectedOrg.name !== currentOrg) {
        updateUrl(selectedGenre, selectedOrg);
      }
    } else if (!selectedGenre && !selectedOrg) {
      // Only navigate to base URL if we're currently on a parameterized URL and not processing URL params
      if ((urlGenreName || urlOrgName) && genres.length > 0) {
        updateUrl(null, null);
      }
    }
  }, [selectedGenre, selectedOrg, navigate, urlGenreName, urlOrgName, genres.length]);

  // REMOVED getSortedPrograms function as sorting is now backend-driven

  return (
    <div className="flex-1 p-6 overflow-auto">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-semibold mb-6">{t('sports.title')}</h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="flex flex-col space-y-6">
          {/* Genre and Organization Selection */}
           <div className="flex space-x-6">
            {/* Genre Dropdown */}
            <div className="flex-1">
              <label htmlFor="genre" className="block text-sm font-medium text-gray-700 mb-2">
                {t('sports.genreLabel')}
              </label>
              <select
                id="genre"
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                onChange={handleGenreChange}
                value={selectedGenre?.genreId || ''}
                disabled={loading || genres.length === 0}
              >
                <option value="">{t('sports.selectGenre')}</option>
                {genres.map((genre) => (
                  <option key={genre.genreId} value={genre.genreId}>
                    {genre.genre}
                  </option>
                ))}
              </select>
              {/* Favourite Sports Buttons */}
              {favouriteSports && favouriteSports.trim() !== '' && genres.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-sm font-medium text-gray-500 mr-2 self-center">{t('sports.favourites')}</span>
                  {favouriteSports.split(',').map(sport => sport.trim()).filter(Boolean).map((sportName, index) => (
                    <button
                      key={index}
                      onClick={() => handleFavouriteSportClick(sportName)}
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${
                        selectedGenre?.genre?.toLowerCase() === sportName.toLowerCase()
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {sportName}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Organization Dropdown */}
            <div className="flex-1">
              <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                {t('sports.organizationLabel')}
              </label>
              <select
                id="organization"
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                onChange={handleOrgChange}
                value={selectedOrg?.organizationId || ''}
                disabled={loading || !selectedGenre || organizations.length === 0}
              >
                <option value="">{t('sports.selectOrganization')}</option>
                {organizations.map((org) => (
                  <option key={org.organizationId} value={org.organizationId}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Quick Selection Buttons */}
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('sports.quickSelect')}
              </label>
              <div className="flex flex-col space-y-2">
                <button
                  onClick={() => handleQuickSelection('Hockey', 'NHL Hockey')}
                  disabled={loading || genres.length === 0}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    selectedGenre?.genre?.toLowerCase() === 'hockey' && selectedOrg?.name === 'NHL Hockey'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  NHL
                </button>
                <button
                  onClick={() => handleQuickSelection('Basketball', 'NBA Basketball')}
                  disabled={loading || genres.length === 0}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    selectedGenre?.genre?.toLowerCase() === 'basketball' && selectedOrg?.name === 'NBA Basketball'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  NBA
                </button>
                <button
                  onClick={() => handleQuickSelection('Football', 'NFL Football')}
                  disabled={loading || genres.length === 0}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    selectedGenre?.genre?.toLowerCase() === 'football' && selectedOrg?.name === 'NFL Football'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  NFL
                </button>
                <button
                  onClick={() => handleQuickSelection('Baseball', 'MLB Baseball')}
                  disabled={loading || genres.length === 0}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    selectedGenre?.genre?.toLowerCase() === 'baseball' && selectedOrg?.name === 'MLB Baseball'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  MLB
                </button>
              </div>
            </div>
          </div>

          {/* Selected Organization Details */}
           {orgDetails && (
            <div className="mt-6 p-4 border border-gray-200 rounded-md">
              <h2 className="text-lg font-medium mb-4">{t('sports.selectedOrgDetails')}</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    {/* Ensure all headers are text-left */}
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('sports.columns.orgId')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('sports.columns.name')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('sports.columns.genre')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('sports.columns.genreId')}
                      </th>
                      {orgDetails.official && (
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('sports.columns.official')}
                        </th>
                      )}
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('sports.columns.rootId')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('sports.columns.updateDate')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('sports.columns.assets')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('sports.columns.conferences')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <React.Fragment>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-left">
                          {orgDetails.organizationId}
                        </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                        {orgDetails.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                        {orgDetails.genre}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                        {orgDetails.genreId}
                      </td>
                      {orgDetails.official && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                          {orgDetails.official}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                        {orgDetails.rootId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                        {orgDetails.updateDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                        <div className="flex flex-wrap gap-2">
                          {orgDetails.assets && orgDetails.assets.length > 0 ? (
                            orgDetails.assets.map((asset, index) => (
                              <div key={index} className="asset-thumbnail" onClick={() => openImagePopup(asset)}>
                                <img 
                                  src={asset.thumbnailUrl} 
                                  alt={`${asset.category || t('sports.asset')} ${index + 1}`} 
                                  title={`${asset.category || t('sports.asset')} (${asset.width}x${asset.height})`}
                                  className="cursor-pointer hover:opacity-80 transition-opacity"
                                />
                              </div>
                            ))
                          ) : (
                            <span>{t('sports.noAssets')}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                        <button
                          onClick={() => setShowConferences(!showConferences)}
                          disabled={!conferences || conferences.length === 0}
                          className={`text-xs px-2 py-1 rounded ${
                            !conferences || conferences.length === 0
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                        >
                          {showConferences ? t('sports.hide') : t('sports.view')} ({conferences?.length || 0})
                        </button>
                      </td>
                    </tr>
                    {/* Expandable Conferences Row */}
                    {showConferences && conferences && conferences.length > 0 && (
                      <tr>
                        {/* Calculate colspan based on number of headers */}
                        <td colSpan={orgDetails.official ? 9 : 8} className="p-4 bg-gray-50 text-left"> 
                          <h3 className="text-md font-medium mb-2 text-gray-700">{t('sports.columns.conferences')}</h3>
                          <div className="overflow-x-auto border rounded-md">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('sports.conferences.id')}
                                  </th>
                                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('sports.conferences.name')}
                                  </th>
                                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('sports.conferences.abbr')}
                                  </th>
                                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('sports.conferences.orgId')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {conferences.map((conference) => (
                                  <tr key={conference.conferenceId}>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 text-left">
                                      {conference.conferenceId}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-left">
                                      {conference.name}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-left">
                                      {conference.abbreviation}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-left">
                                      {conference.organizationId}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Image Popup */}
           {selectedImage && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={closeImagePopup}>
              <div className="relative max-w-4xl max-h-screen p-4" onClick={e => e.stopPropagation()}>
                <button 
                  className="absolute top-2 right-2 bg-white rounded-full p-2 text-gray-800 hover:bg-gray-200"
                  onClick={closeImagePopup}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="bg-white p-4 rounded-lg shadow-lg">
                  <img 
                    src={selectedImage.url} 
                    alt={selectedImage.category || t('sports.fullSizeImage')} 
                    className="max-h-[80vh] max-w-full object-contain"
                  />
                  <div className="mt-4 text-sm">
                    <p><strong>{t('sports.image.category')}</strong> {selectedImage.category || 'N/A'}</p>
                    <p><strong>{t('sports.image.dimensions')}</strong> {selectedImage.width}x{selectedImage.height}</p>
                    <p><strong>{t('sports.image.type')}</strong> {selectedImage.type || 'N/A'}</p>
                    <p><strong>{t('sports.image.assetId')}</strong> {selectedImage.assetId || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sports Connectors Table */}
          {selectedOrg && connectors.length > 0 && (
            <div className="mt-6 p-4 border border-gray-200 rounded-md">
              <h2 className="text-lg font-medium mb-4">{t('sports.connectors.title')}</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                   <thead className="bg-gray-50">
                    <tr>
                      {/* Add header for the clear button column */}
                      <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10"> {/* Fixed width */}
                        {/* Intentionally blank */}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('sports.connectors.connectorId')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('sports.connectors.titleHeader')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('sports.connectors.language')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('sports.connectors.programCount')}
                      </th>
                      {/* Removed Actions header */}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Filter connectors based on selection */}
                    {(selectedConnector ? connectors.filter(c => c.connectorid === selectedConnector.connectorid) : connectors)
                    .map((connector) => (
                      <React.Fragment key={connector.connectorid}>
                         {/* Make the entire row clickable for selection */}
                         <tr 
                          className={`hover:bg-gray-50 cursor-pointer ${selectedConnector && selectedConnector.connectorid === connector.connectorid ? "bg-blue-50" : ""}`}
                          onClick={() => handleConnectorClick(connector)} // Select on row click
                        >
                          {/* Clear button cell */}
                          <td className="px-2 py-4 whitespace-nowrap text-center">
                            {selectedConnector && selectedConnector.connectorid === connector.connectorid && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click handler
                                  handleClearConnectorSelection();
                                }}
                                className="text-red-500 hover:text-red-700 font-bold text-lg leading-none align-middle" // Adjusted styling
                                title="Clear selection and show all connectors"
                                style={{ lineHeight: '1' }} // Ensure button text aligns well
                              >
                                &times; {/* Simple 'X' character */}
                              </button>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-left">
                            <CopyableId 
                              value={connector.connectorid} 
                              onValueClick={(e) => { 
                                e.stopPropagation(); // Prevent row click handler
                                loadProgramById(connector.connectorid, true); 
                              }} 
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                            {connector.title}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                            {connector.lang}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                            {connector.numPrograms || 0}
                          </td>
                          {/* Removed Actions cell */}
                        </tr>
                          {/* Expanded row for seasons/programs */}
                        {selectedConnector && selectedConnector.connectorid === connector.connectorid && seasons && seasons.seasons && (
                          <tr>
                            {/* Adjust colSpan to account for the new clear button column */}
                            <td colSpan="5" className="px-6 py-4 text-sm text-gray-500 bg-gray-50 text-left"> 
                               <div className="mb-2">
                                <span className="font-medium">{t('sports.connectors.connectorId')}:</span> {seasons.connectorid} | <span className="font-medium">{t('sports.connectors.totalPrograms')}</span> {seasons.program_count}
                              </div>
                              {seasons.seasons.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {seasons.seasons.map((seasonData, index) => (
                                    <div 
                                      key={index} 
                                      className={`px-3 py-1 bg-gray-100 rounded-md text-gray-800 hover:bg-gray-200 transition-colors cursor-pointer ${
                                        selectedSeason === (typeof seasonData === 'object' ? seasonData.season : seasonData) ? 'bg-blue-100 border border-blue-400' : ''
                                      }`}
                                      onClick={() => handleSeasonClick(seasonData)}
                                    >
                                      {typeof seasonData === 'object' && seasonData.season ? 
                                        `${seasonData.season} (${seasonData.program_count} ${t('sports.connectors.programs')})` : 
                                        seasonData}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p>{t('sports.connectors.noSeasons')}</p>
                              )}
                              {/* Programs Table */}
                              {selectedSeason && ( // Show table container even if programs is empty initially
                                <div className="mt-4">
                                  <h3 className="text-md font-medium mb-2">
                                    {t('sports.programs.title', { season: selectedSeason })} 
                                    {pagination.total > 0 && ` (${pagination.total} ${t('sports.programs.total')})`}
                                  </h3>
                                  {programs.length > 0 ? (
                                    <>
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                          <thead className="bg-gray-100">
                                            <tr>
                                              {/* Update sort indicators based on pagination state */}
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('TMSId')}>
                                                {t('sports.programs.tmsId')} {pagination.sort_by === 'TMSId' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('title')}>
                                                {t('sports.programs.programTitle')} {pagination.sort_by === 'title' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('home_team')}>
                                                {t('sports.programs.homeTeam')} {pagination.sort_by === 'home_team' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('away_team')}>
                                                {t('sports.programs.awayTeam')} {pagination.sort_by === 'away_team' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('gameDate')}>
                                                {t('sports.programs.gameDate')} {pagination.sort_by === 'gameDate' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('gameTime')}>
                                                {t('sports.programs.gameTime')} {pagination.sort_by === 'gameTime' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('gameTimeZone')}>
                                                {t('sports.programs.timeZone')} {pagination.sort_by === 'gameTimeZone' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('lang')}>
                                                {t('sports.programs.lang')} {pagination.sort_by === 'lang' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('playoffRound')}>
                                                {t('sports.programs.playoffRound')} {pagination.sort_by === 'playoffRound' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('playoffRound_gameNum')}>
                                                {t('sports.programs.gameNum')} {pagination.sort_by === 'playoffRound_gameNum' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('rootId')}>
                                                {t('sports.columns.rootId')} {pagination.sort_by === 'rootId' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('season_type')}>
                                                {t('sports.programs.seasonType')} {pagination.sort_by === 'season_type' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('venue')}>
                                                {t('sports.programs.venue')} {pagination.sort_by === 'venue' && (<span>{pagination.sort_order === 'asc' ? '↑' : '↓'}</span>)}
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody className="bg-white divide-y divide-gray-200">
                                            {/* Render programs directly from state */}
                                            {programs.map((program) => (
                                              <tr key={program.TMSId}>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                  <div className="flex items-center gap-1">
                                                    <CopyableId 
                                                      value={program.TMSId} 
                                                      onValueClick={() => loadProgramById(program.TMSId, true)} 
                                                      title="View program details and copy ID"
                                                    />
                                                    <SaveButton 
                                                      tmsId={program.TMSId}
                                                      buttonClassName="ml-1"
                                                    />
                                                  </div>
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{program.title || 'N/A'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{program.home_team?.name || 'N/A'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{program.away_team?.name || 'N/A'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{program.gameDate || 'N/A'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{program.gameTime || 'N/A'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{program.gameTimeZone || 'N/A'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{program.lang || 'N/A'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{program.playoffRound || 'N/A'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{program.playoffRound_gameNum || 'N/A'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                  {program.rootId ? <CopyableId value={program.rootId} /> : 'N/A'}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{program.season_type || 'N/A'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{program.venue || 'N/A'}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                      {/* Pagination Controls */}
                                      {pagination.total_pages > 1 && (
                                        <div className="mt-4 flex justify-between items-center">
                                          <span className="text-sm text-gray-700">
                                            {t('searchResults.showingPage', { page: pagination.page, totalPages: pagination.total_pages, totalResults: pagination.total })}
                                          </span>
                                          <div>
                                            <button
                                              onClick={() => handlePageChange(currentPage - 1)}
                                              disabled={currentPage <= 1 || loading}
                                              className="px-3 py-1 text-sm border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                              {t('sports.pagination.previous')}
                                            </button>
                                            <button
                                              onClick={() => handlePageChange(currentPage + 1)}
                                              disabled={currentPage >= pagination.total_pages || loading}
                                              className="ml-2 px-3 py-1 text-sm border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                              {t('sports.pagination.next')}
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    !loading && (
                                      <div className="mt-4 text-gray-500">
                                        {t('sports.programs.noPrograms')}
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}


          {/* Loading Indicator */}
          {loading && (
            <div className="flex justify-center items-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SportsOrgsPage;
